import React, { useState } from 'react';
import { X, BookOpen, Copy, Check, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';

// ─── Data ─────────────────────────────────────────────────────────────────────

const TOOLS_ES = [
  {
    id: '01', icon: '💻', title: 'SAP - PROGRAMA Z',
    desc: 'Genera programas ejecutables completos con arquitectura modular de includes.',
    steps: ['Ir a SAP - PROGRAMA Z', 'Seleccionar S/4HANA 2021+ como versión target', 'Pegar el texto de ejemplo en REQUERIMIENTO IA', 'Hacer clic en GENERAR ARQUITECTURA Z'],
    example: `Crear programa ZFI_AR_AUTO_CLEARING para compensación automática de facturas por avisos de pago por email.
Módulo: FI-AR. Tablas: ZPAY_EMAIL_HDR, ZPAY_EMAIL_POS, ZCLIENT_EMAIL, BSID, FEBEP.
Proceso de 8 pasos con matching y scoring.
Output: ALV con resultados de compensación.
Autor: [Tu nombre]. Versión: S/4HANA 2021+`,
    tip: 'Subí tu especificación técnica real como PDF o Word para mejores resultados.',
  },
  {
    id: '02', icon: '📊', title: 'SAP - REPORTES ALV',
    desc: 'Genera reportes ALV profesionales con CL_SALV_TABLE y columnas configuradas.',
    steps: ['Ir a SAP - REPORTES ALV', 'Seleccionar S/4HANA 2021+', 'Pegar el ejemplo en REQUERIMIENTO IA', 'Hacer clic en GENERAR REPORTE ALV'],
    example: `Reporte ALV de pedidos de compra EKKO/EKPO.
Columnas: Sociedad, Pedido, Proveedor, Nombre, Fecha, Monto Neto, Moneda.
Filtros: Sociedad (obligatorio), Rango de pedidos.
Hotspot en número de pedido para navegar a ME23N.
Funciones: exportar, ordenar, filtrar.`,
    tip: 'Sin especificación el motor genera un ejemplo estándar con EKKO/EKPO automáticamente.',
  },
  {
    id: '03', icon: '🗄️', title: 'SAP - OPTIMIZADOR SQL',
    desc: 'Detecta problemas de performance y reescribe los SELECT para HANA.',
    steps: ['Ir a SAP - OPTIMIZADOR SQL', 'Pegar el código ineficiente directamente en REQUERIMIENTO IA', 'Hacer clic en OPTIMIZAR SENTENCIA SQL'],
    example: `SELECT * FROM bkpf INTO TABLE lt_bkpf.
LOOP AT lt_bkpf INTO ls_bkpf.
  SELECT * FROM bseg INTO TABLE lt_bseg
    WHERE belnr = ls_bkpf-belnr.
ENDLOOP.
SELECT * FROM kna1 INTO TABLE lt_kna1
  WHERE land1 = 'AR'.`,
    tip: 'El motor detecta SELECT en LOOP, SELECT *, falta de filtros y sugiere índices.',
  },
  {
    id: '04', icon: '⚡', title: 'SAP - OPTIMIZACIÓN RENDIMIENTO',
    desc: 'Implementa Field Symbols, Binary Search y elimina Nested Loops.',
    steps: ['Ir a SAP - OPTIMIZACIÓN RENDIMIENTO', 'Seleccionar área crítica y volumen de datos', 'Pegar el código en REQUERIMIENTO IA', 'Hacer clic en OPTIMIZAR RENDIMIENTO'],
    example: `SELECT * FROM vbak INTO TABLE lt_vbak.
LOOP AT lt_vbak INTO ls_vbak.
  SELECT * FROM vbap INTO TABLE lt_vbap
    WHERE vbeln = ls_vbak-vbeln.
  LOOP AT lt_vbap INTO ls_vbap.
    MOVE-CORRESPONDING ls_vbap TO ls_result.
    APPEND ls_result TO lt_result.
  ENDLOOP.
ENDLOOP.
DESCRIBE TABLE lt_result LINES lv_count.`,
    tip: 'Marca los checkboxes de Field Symbols y Avoid Nested Loops para mejores resultados.',
  },
  {
    id: '05', icon: '🔄', title: 'SAP - MODERNIZACIÓN SINTAXIS',
    desc: 'Convierte código legacy ECC a sintaxis moderna S/4HANA.',
    steps: ['Ir a SAP - MODERNIZACIÓN SINTAXIS', 'Pegar el código clásico en REQUERIMIENTO IA', 'Hacer clic en MODERNIZAR CÓDIGO'],
    example: `DATA: lv_nombre TYPE string,
      lv_resultado TYPE string,
      lv_count TYPE i.
CONCATENATE lv_nombre ' SAP' INTO lv_resultado.
DESCRIBE TABLE lt_clientes LINES lv_count.
CREATE OBJECT lo_objeto.
CALL METHOD lo_objeto->procesar( ).
IF NOT lv_nombre IS INITIAL.
  ADD 1 TO lv_count.
ENDIF.`,
    tip: 'El motor moderniza CONCATENATE, DESCRIBE TABLE, CREATE OBJECT, CALL METHOD y más.',
  },
  {
    id: '06', icon: '🚀', title: 'SAP - S/4HANA MIGRATION',
    desc: 'Identifica tablas obsoletas y migra al modelo universal ACDOCA.',
    steps: ['Ir a SAP - S/4HANA MIGRATION', 'Pegar el código ECC en REQUERIMIENTO IA', 'Hacer clic en ANALIZAR MIGRACIÓN'],
    example: `SELECT * FROM bsis INTO TABLE lt_bsis
  WHERE bukrs = '1000' AND gjahr = '2024'.
SELECT * FROM bsas INTO TABLE lt_bsas
  WHERE bukrs = '1000' AND gjahr = '2024'.
SELECT * FROM bsid INTO TABLE lt_bsid
  WHERE bukrs = '1000' AND kunnr = '100001'.
* Campo obsoleto en S/4HANA:
WRITE: ls_mara-mtvfp.`,
    tip: 'BSIS, BSAS, BSID, BSAD no existen en S/4HANA — el motor las migra a ACDOCA.',
  },
  {
    id: '07', icon: '🌊', title: 'SAP - FLUJO DE NEGOCIO',
    desc: 'Genera diagramas Mermaid.js con el flujo lógico del proceso.',
    steps: ['Ir a SAP - FLUJO DE NEGOCIO', 'Describir el proceso en REQUERIMIENTO IA', 'Hacer clic en DISEÑAR FLUJO LÓGICO'],
    example: `Flujo de compensación automática de facturas:
1. Recepción de email con aviso de pago
2. Identificación del cliente por email
3. Validación de facturas abiertas en BSID
4. Validación del total vs suma de posiciones
5. Búsqueda en extracto bancario FEBEP
6. Matching con scoring (importe, fecha, texto)
7. Decisión: automático >= 80, manual >= 50, error < 50
8. Compensación y registro de log`,
    tip: 'El motor genera código Mermaid listo para pegar en mermaid.live o Confluence.',
  },
  {
    id: '08', icon: '🏗️', title: 'SAP - MODELADOR VISTAS CDS',
    desc: 'Diseña vistas CDS con anotaciones @UI, @OData y associations.',
    steps: ['Ir a SAP - MODELADOR VISTAS CDS', 'Describir la vista en REQUERIMIENTO IA', 'Hacer clic en GENERAR VISTA CDS'],
    example: `Vista CDS ZC_PEDIDOS_COMPRA basada en EKKO/EKPO.
Campos: ebeln, ebelp, bukrs, lifnr, name1, bedat, netwr, waers, matnr, menge.
JOIN con LFA1 para nombre del proveedor.
Anotaciones OData para consumo Fiori.
Filtros en: bukrs, lifnr, bedat.
Association a LFA1 por lifnr.`,
    tip: 'Sin especificación genera automáticamente un ejemplo estándar con EKKO/EKPO/LFA1.',
  },
  {
    id: '08b', icon: '🔗', title: 'SAP - CDS COMPOSITE VIEW',
    desc: 'Une dos CDS existentes en una vista Composite + Consumption. No requiere tablas físicas.',
    steps: [
      'Ir a SAP - CDS COMPOSITE VIEW',
      'Ingresar el nombre de la vista Composite (ZCC_)',
      'Ingresar los nombres de las dos vistas CDS existentes a unir',
      'Configurar las associations entre las vistas',
      'Hacer clic en CREAR VISTA COMPOSITE'
    ],
    example: `Unir ZI_SalesOrder con ZI_SalesItem y ZI_CustomerMaster.
Composite View: ZCC_SalesOrderFull
Consumption View: ZC_SalesOrderFull
Association _Items: ZI_SalesItem por SalesOrder = SalesOrder [0..*]
Association _Customer: ZI_CustomerMaster por CustomerId = CustomerNumber [0..1]
Exponer via OData para Fiori Elements.
Metadata Extension en capa CUSTOMER.`,
    tip: 'El motor genera automáticamente los 3 objetos: Composite + Consumption + Metadata Extension con arquitectura VDM correcta y sin JOINs directos.',
  },
  {
    id: '09', icon: '📱', title: 'SAP - FIORI ELEMENTS UI',
    desc: 'Genera anotaciones para List Report + Object Page en ABAP o CAP/BTP.',
    steps: ['Ir a SAP - FIORI ELEMENTS UI', 'Seleccionar plataforma: ABAP On-Premise o SAP BTP/CAP', 'Describir la app en REQUERIMIENTO IA', 'Hacer clic en GENERAR FIORI'],
    example: `App Fiori Elements para pedidos de compra.
Plataforma: ABAP On-Premise (S/4HANA).
List Report: columnas ebeln, lifnr, bedat, netwr.
Filtros: bukrs, lifnr.
Object Page: datos generales + datos financieros.
Servicio OData v2 en SEGW.
Incluir pasos de activación en /IWFND/MAINT_SERVICE.`,
    tip: 'Genera vista CDS + metadata.xml + annotations.xml + servicio OData + pasos de activación.',
  },
  {
    id: '10', icon: '🐛', title: 'SAP - ST22 RUNTIME ANALYSIS',
    desc: 'Analiza Short Dumps e identifica causa raíz con solución técnica.',
    steps: ['Ir a SAP - ST22 RUNTIME ANALYSIS', 'Pegar el contenido del dump en REQUERIMIENTO IA', 'Hacer clic en ANALIZAR DUMP'],
    example: `Short Dump: SYSTEM_NO_ROLL
Programa: ZFI_AR_AUTO_CLEARING
Include: ZFI_AR_AUTO_CLEARING_F01
Línea: 245
Código en el momento del error:
SELECT * FROM bseg INTO TABLE lt_bseg
  WHERE bukrs = p_bukrs AND belnr IN lr_belnr.
Variables: LT_BSEG: 2.847.392 registros`,
    tip: 'Incluí la mayor cantidad de información del ST22: tipo de dump, variables, stack de llamadas.',
  },
  {
    id: '11', icon: '✅', title: 'SAP - ABAP UNIT TESTING',
    desc: 'Crea clases de prueba con CL_ABAP_UNIT_ASSERT para validar lógica.',
    steps: ['Ir a SAP - ABAP UNIT TESTING', 'Pegar el código a testear en REQUERIMIENTO IA', 'Hacer clic en GENERAR UNIT TESTS'],
    example: `Generar unit tests para la clase lcl_clearing_processor con estos métodos:
- calcular_score: recibe importe_email, importe_banco, fecha_email, fecha_banco. Devuelve score 0-100.
- validar_total: recibe total_header y tabla posiciones. Devuelve abap_bool.
- determinar_accion: recibe score. Devuelve AUTOMATICO/MANUAL/ERROR.
Cubrir casos normales, límites y errores.`,
    tip: 'El motor genera setup/teardown y al menos 3 casos por método con assertions.',
  },
  {
    id: '12', icon: '🔐', title: 'SAP - AUTHORIZATION CHECK',
    desc: 'Detecta accesos sin AUTHORITY-CHECK y agrega seguridad ABAP.',
    steps: ['Ir a SAP - AUTHORIZATION CHECK', 'Pegar el código a auditar en REQUERIMIENTO IA', 'Hacer clic en AUDITAR SEGURIDAD'],
    example: `FORM frm_leer_facturas.
  SELECT bukrs, belnr, gjahr, dmbtr
    INTO TABLE @gt_bkpf FROM bkpf
    WHERE bukrs = @p_bukrs.
ENDFORM.
FORM frm_actualizar_estado.
  UPDATE zpay_email_hdr SET status = @iv_status
    WHERE id_email = @iv_id.
ENDFORM.
FORM frm_borrar_registro.
  DELETE FROM zmatch_log WHERE id = @iv_id.
ENDFORM.`,
    tip: 'El motor identifica qué objeto de autorización SAP corresponde a cada operación.',
  },
  {
    id: '13', icon: '📄', title: 'SAP - TECHNICAL BLUEPRINT',
    desc: 'Genera documentos de diseño técnico profesionales en Markdown.',
    steps: ['Ir a SAP - TECHNICAL BLUEPRINT', 'Describir el programa en REQUERIMIENTO IA', 'Hacer clic en GENERAR BLUEPRINT'],
    example: `Documento técnico para ZFI_AR_AUTO_CLEARING.
Módulo: FI-AR / Bank Accounting.
Tablas Z: ZPAY_EMAIL_HDR, ZPAY_EMAIL_POS, ZCLIENT_EMAIL, ZBANK_MATCH, ZMATCH_LOG.
Tablas estándar: BSID, FEBEP, KNA1.
Versión: S/4HANA 2021+.
Proceso: 8 pasos con matching y scoring.
Autor: [Tu nombre]. Estimación: 40 horas.`,
    tip: 'Genera tablas de campos, matriz de relaciones, pantalla de selección y validaciones.',
  },
  {
    id: '14', icon: '🔗', title: 'SAP - API DATA MAPPING',
    desc: 'Convierte JSON a estructuras TYPES ABAP con deserialización.',
    steps: ['Ir a SAP - API DATA MAPPING', 'Pegar el JSON en REQUERIMIENTO IA', 'Hacer clic en MAPEAR JSON A ABAP'],
    example: `{
  "paymentNotification": {
    "id": "550e8400-e29b-41d4-a716",
    "sender": {
      "email": "proveedor@empresa.com",
      "taxId": "30-12345678-9"
    },
    "header": {
      "totalAmount": 15750.50,
      "currency": "ARS"
    },
    "status": "PENDING"
  }
}`,
    tip: 'Genera TYPES ABAP, deserialización con /ui2/cl_json y validación contra tablas SAP.',
  },
  {
    id: '15', icon: '🌐', title: 'SAP - CODE TRANSLATION',
    desc: 'Traduce comentarios y mensajes del inglés al español técnico SAP.',
    steps: ['Ir a SAP - CODE TRANSLATION', 'Pegar el código en inglés en REQUERIMIENTO IA', 'Hacer clic en TRADUCIR CÓDIGO'],
    example: `* Select pending payment notices
FORM frm_select_payments.
  * Read header table
  SELECT mandt, id_email, email_from, total
    INTO TABLE @gt_pay_hdr
    FROM zpay_email_hdr
    WHERE status = @gc_status_pending.
  * Validate results
  IF sy-subrc <> 0.
    * No records found
    MESSAGE i001 WITH 'No pending payments'.
  ENDIF.
ENDFORM.`,
    tip: 'Traduce SOLO comentarios y mensajes — el código, variables y sintaxis no se tocan.',
  },
  {
    id: '16', icon: '📋', title: 'SAP - DOCUMENT GENERATION',
    desc: 'Genera manuales de usuario paso a paso en Markdown.',
    steps: ['Ir a SAP - DOCUMENT GENERATION', 'Describir el programa en REQUERIMIENTO IA', 'Hacer clic en GENERAR DOCUMENTO'],
    example: `Manual de usuario para ZFI_AR_AUTO_CLEARING.
El programa procesa avisos de pago por email y compensa automáticamente las facturas.
Pasos: ejecutar SE38, ingresar sociedad y fechas, activar modo TEST, ver resultado en ALV.
ALV muestra: Email, Cliente, Score, Estado.
Score >= 80: automático. Score >= 50: manual.
Mensajes: 'Sin avisos', 'Cliente no identificado', 'Compensación exitosa', 'Error en compensación'.`,
    tip: 'Genera pasos numerados con capturas indicadas, mensajes del sistema y FAQ completo.',
  },
  {
    id: '17', icon: '🔍', title: 'SAP - VALIDADOR DE ESPECIFICACIÓN',
    desc: 'Analiza documentos funcionales y evalúa completitud contra el modelo estándar de 10 secciones.',
    steps: ['Ir a SAP - VALIDADOR DE ESPECIFICACIÓN', 'Subir el documento funcional (PDF o Word)', 'Hacer clic en VALIDAR ESPECIFICACIÓN', 'Ver el reporte con score y detalle por sección', 'Opcionalmente hacer clic en GENERAR DOCUMENTO CORREGIDO'],
    example: `Subir cualquier documento funcional SAP en formato PDF o Word.
El validador evalúa las 10 secciones estándar:
1. Objetivo del Desarrollo
2. Datos del Solicitante
3. Alcance Funcional
4. Reglas de Negocio
5. Flujo del Proceso
6. Interfaces y Formularios
7. Modelo de Datos / Tablas SAP
8. Reportes y Salidas
9. Seguridad y Autorizaciones
10. Criterios de Aceptación`,
    tip: 'El score va de 0 a 100%. Documentos con score < 50% pueden regenerarse automáticamente.',
  },
  {
    id: '18', icon: '🔷', title: 'SAP - RAP ARCHITECT',
    desc: 'Genera el stack RAP completo S/4HANA con 13 objetos listos para activar en Eclipse ADT.',
    steps: [
      'Ir a SAP - RAP ARCHITECT',
      'Paso 1 — Entidad Base: ingresá nombre, tabla DDIC, campos clave y campos de datos',
      'Paso 2 — CDS Stack: definí los nombres de ZI_, ZR_, ZC_ y activá Draft si necesitás borradores',
      'Paso 3 — Behavior: seleccioná operaciones CRUD, validaciones y actions personalizadas',
      'Paso 4 — Service: definí el nombre del Service Definition y Binding, seleccioná OData V4',
      'Hacer clic en GENERAR y explorar los 13 objetos en el RAP Stack Explorer',
    ],
    example: `Entidad: SalesOrder | Tabla: VBAK
Campos clave: Id (abap.uuid), Vbeln (vbeln)
Campos de datos: Kunnr (kunnr), Erdat (abap.dats), Netwr (abap.dec(15,2)), Waers (waers)
Draft: activado
Validaciones: validateNetAmount, validateCustomer
Service: ZSD_SALESORDER → ZSB_SALESORDER_V4 (OData V4)

Objetos generados:
- ZSALESORDER + ZSALESORDER_D (tablas activa y draft)
- ZI_SALESORDER (Interface View)
- ZR_SALESORDER (Root BO View)
- ZC_SALESORDER (Projection View con transactional_ui)
- ZC_SALESORDER.ddlx (Metadata Extension con @UI completo)
- ZR_SALESORDER.bdef (managed + strict(2) + with draft)
- ZBP_R_SALESORDER (Behavior Implementation con validaciones)
- ZC_SALESORDER.bdef (Projection BDEF con use draft)
- ZI_SALESORDER.dcl + ZC_SALESORDER.dcl (Access Control)
- ZSD_SALESORDER (Service Definition)
- ZSB_SALESORDER_V4 (Service Binding OData V4)`,
    tip: 'Activar en Eclipse ADT en orden: Tablas → ZI_ → ZR_ → ZC_ → Metadata Ext → BDEF → Service. Sin ese orden hay errores de activación.',
  },
  {
    id: '19', icon: '☁️', title: 'SAP - CAP ARCHITECT',
    desc: 'Genera el stack CAP completo para SAP BTP con 13 archivos listos para cds watch y deploy.',
    steps: [
      'Ir a SAP - CAP ARCHITECT',
      'Paso 1 — Proyecto: ingresá nombre, runtime (Node.js/Java), database (SQLite/HANA) y namespace',
      'Paso 2 — Modelo: definí las entidades con sus campos y tipos CDS',
      'Paso 3 — Servicio: configurá el nombre del servicio, OData version y anotaciones Fiori',
      'Paso 4 — Deploy: activá mta.yaml, XSUAA y AppRouter si vas a deployar en BTP',
      'Hacer clic en GENERAR y explorar los 13 archivos en el CAP Stack Explorer',
    ],
    example: `Proyecto: orders-app | Runtime: Node.js | DB: SQLite | Namespace: com.orquest

Entidad 1 — Orders:
  ID (UUID, key), customerId (String), orderDate (Date), netAmount (Decimal), status (String)

Entidad 2 — OrderItems:
  ID (UUID, key), quantity (Integer), unitPrice (Decimal)

Servicio: CatalogService | OData V4 | Fiori Annotations: ✅

Archivos generados:
- db/schema.cds (entidades con composiciones y enums)
- srv/service.cds (definición del servicio OData V4)
- srv/annotations.cds (anotaciones Fiori: HeaderInfo, LineItem, Facets)
- srv/service.js (handlers before/after + validaciones + actions)
- package.json (dependencias @sap/cds correctas)
- mta.yaml (módulos srv, db, approuter para BTP)
- xs-security.json (roles y scopes XSUAA)
- .cdsrc.json (SQLite local / HANA producción)
- .gitignore + .env (configuración local)
- db/data/Orders.csv + db/data/OrderItems.csv (datos de prueba)
- README.md (pasos de deploy con cf y MTA)

Para correr local: npm install → cds watch → http://localhost:4004`,
    tip: 'Usá SQLite para desarrollo local (sin configuración) y switcheá a HANA en producción cambiando solo el .cdsrc.json.',
  },
];

const TOOLS_EN = [
  {
    id: '01', icon: '💻', title: 'SAP - PROGRAM Z',
    desc: 'Generates complete executable programs with modular include architecture.',
    steps: ['Go to SAP - PROGRAM Z', 'Select S/4HANA 2021+ as the target version', 'Paste the example text into the AI REQUIREMENT field', 'Click GENERATE Z ARCHITECTURE'],
    example: `Create program ZFI_AR_AUTO_CLEARING for automatic invoice clearing based on email payment notices.
Module: FI-AR. Tables: ZPAY_EMAIL_HDR, ZPAY_EMAIL_POS, ZCLIENT_EMAIL, BSID, FEBEP.
8-step process with matching and scoring.
Output: ALV with clearing results.
Author: [Your name]. Version: S/4HANA 2021+`,
    tip: 'Upload your real technical specification as PDF or Word for best results.',
  },
  {
    id: '02', icon: '📊', title: 'SAP - ALV REPORTS',
    desc: 'Generates professional ALV reports with CL_SALV_TABLE and configured columns.',
    steps: ['Go to SAP - ALV REPORTS', 'Select S/4HANA 2021+', 'Paste the example into the AI REQUIREMENT field', 'Click GENERATE ALV REPORT'],
    example: `ALV report for purchase orders EKKO/EKPO.
Columns: Company, Order, Vendor, Name, Date, Net Amount, Currency.
Filters: Company (mandatory), Order range.
Hotspot on order number to navigate to ME23N.
Features: export, sort, filter.`,
    tip: 'Without a specification the engine automatically generates a standard EKKO/EKPO example.',
  },
  {
    id: '03', icon: '🗄️', title: 'SAP - SQL OPTIMIZER',
    desc: 'Detects performance issues and rewrites SELECT statements for HANA.',
    steps: ['Go to SAP - SQL OPTIMIZER', 'Paste the inefficient code directly into the AI REQUIREMENT field', 'Click OPTIMIZE SQL STATEMENT'],
    example: `SELECT * FROM bkpf INTO TABLE lt_bkpf.
LOOP AT lt_bkpf INTO ls_bkpf.
  SELECT * FROM bseg INTO TABLE lt_bseg
    WHERE belnr = ls_bkpf-belnr.
ENDLOOP.
SELECT * FROM kna1 INTO TABLE lt_kna1
  WHERE land1 = 'AR'.`,
    tip: 'The engine detects SELECT in LOOP, SELECT *, missing filters and suggests indexes.',
  },
  {
    id: '04', icon: '⚡', title: 'SAP - PERFORMANCE OPTIMIZATION',
    desc: 'Implements Field Symbols, Binary Search and eliminates Nested Loops.',
    steps: ['Go to SAP - PERFORMANCE OPTIMIZATION', 'Select critical area and data volume', 'Paste the code into the AI REQUIREMENT field', 'Click OPTIMIZE PERFORMANCE'],
    example: `SELECT * FROM vbak INTO TABLE lt_vbak.
LOOP AT lt_vbak INTO ls_vbak.
  SELECT * FROM vbap INTO TABLE lt_vbap
    WHERE vbeln = ls_vbak-vbeln.
  LOOP AT lt_vbap INTO ls_vbap.
    MOVE-CORRESPONDING ls_vbap TO ls_result.
    APPEND ls_result TO lt_result.
  ENDLOOP.
ENDLOOP.
DESCRIBE TABLE lt_result LINES lv_count.`,
    tip: 'Check the Field Symbols and Avoid Nested Loops checkboxes for best results.',
  },
  {
    id: '05', icon: '🔄', title: 'SAP - SYNTAX MODERNIZATION',
    desc: 'Converts legacy ECC code to modern S/4HANA syntax.',
    steps: ['Go to SAP - SYNTAX MODERNIZATION', 'Paste the classic code into the AI REQUIREMENT field', 'Click MODERNIZE CODE'],
    example: `DATA: lv_name TYPE string,
      lv_result TYPE string,
      lv_count TYPE i.
CONCATENATE lv_name ' SAP' INTO lv_result.
DESCRIBE TABLE lt_customers LINES lv_count.
CREATE OBJECT lo_object.
CALL METHOD lo_object->process( ).
IF NOT lv_name IS INITIAL.
  ADD 1 TO lv_count.
ENDIF.`,
    tip: 'The engine modernizes CONCATENATE, DESCRIBE TABLE, CREATE OBJECT, CALL METHOD and more.',
  },
  {
    id: '06', icon: '🚀', title: 'SAP - S/4HANA MIGRATION',
    desc: 'Identifies obsolete tables and migrates to the universal ACDOCA model.',
    steps: ['Go to SAP - S/4HANA MIGRATION', 'Paste the ECC code into the AI REQUIREMENT field', 'Click ANALYZE MIGRATION'],
    example: `SELECT * FROM bsis INTO TABLE lt_bsis
  WHERE bukrs = '1000' AND gjahr = '2024'.
SELECT * FROM bsas INTO TABLE lt_bsas
  WHERE bukrs = '1000' AND gjahr = '2024'.
SELECT * FROM bsid INTO TABLE lt_bsid
  WHERE bukrs = '1000' AND kunnr = '100001'.
* Obsolete field in S/4HANA:
WRITE: ls_mara-mtvfp.`,
    tip: 'BSIS, BSAS, BSID, BSAD do not exist in S/4HANA — the engine migrates them to ACDOCA.',
  },
  {
    id: '07', icon: '🌊', title: 'SAP - BUSINESS FLOW',
    desc: 'Generates Mermaid.js diagrams with the logical process flow.',
    steps: ['Go to SAP - BUSINESS FLOW', 'Describe the process in the AI REQUIREMENT field', 'Click DESIGN LOGICAL FLOW'],
    example: `Automatic invoice clearing flow:
1. Receive email with payment notice
2. Identify customer by email
3. Validate open invoices in BSID
4. Validate total vs sum of line items
5. Search bank statement FEBEP
6. Matching with scoring (amount, date, text)
7. Decision: automatic >= 80, manual >= 50, error < 50
8. Clearing and log registration`,
    tip: 'The engine generates Mermaid code ready to paste in mermaid.live or Confluence.',
  },
  {
    id: '08', icon: '🏗️', title: 'SAP - CDS VIEW MODELER',
    desc: 'Designs CDS views with @UI, @OData annotations and associations.',
    steps: ['Go to SAP - CDS VIEW MODELER', 'Describe the view in the AI REQUIREMENT field', 'Click GENERATE CDS VIEW'],
    example: `CDS View ZC_PURCHASE_ORDERS based on EKKO/EKPO.
Fields: ebeln, ebelp, bukrs, lifnr, name1, bedat, netwr, waers, matnr, menge.
JOIN with LFA1 for vendor name.
OData annotations for Fiori consumption.
Filters on: bukrs, lifnr, bedat.
Association to LFA1 by lifnr.`,
    tip: 'Without a specification it automatically generates a standard example with EKKO/EKPO/LFA1.',
  },
  {
    id: '08b', icon: '🔗', title: 'SAP - CDS COMPOSITE VIEW',
    desc: 'Joins two existing CDS views into a Composite + Consumption stack. No physical tables required.',
    steps: [
      'Go to SAP - CDS COMPOSITE VIEW',
      'Enter the Composite view name (ZCC_)',
      'Enter the two existing CDS view names to join',
      'Configure associations between views',
      'Click CREATE COMPOSITE VIEW'
    ],
    example: `Join ZI_SalesOrder with ZI_SalesItem and ZI_CustomerMaster.
Composite View: ZCC_SalesOrderFull
Consumption View: ZC_SalesOrderFull
Association _Items: ZI_SalesItem by SalesOrder = SalesOrder [0..*]
Association _Customer: ZI_CustomerMaster by CustomerId = CustomerNumber [0..1]
Expose via OData for Fiori Elements.
Metadata Extension in CUSTOMER layer.`,
    tip: 'The engine automatically generates 3 objects: Composite + Consumption + Metadata Extension with correct VDM architecture and no direct JOINs.',
  },
  {
    id: '09', icon: '📱', title: 'SAP - FIORI ELEMENTS UI',
    desc: 'Generates annotations for List Report + Object Page in ABAP or CAP/BTP.',
    steps: ['Go to SAP - FIORI ELEMENTS UI', 'Select platform: ABAP On-Premise or SAP BTP/CAP', 'Describe the app in the AI REQUIREMENT field', 'Click GENERATE FIORI'],
    example: `Fiori Elements app for purchase orders.
Platform: ABAP On-Premise (S/4HANA).
List Report: columns ebeln, lifnr, bedat, netwr.
Filters: bukrs, lifnr.
Object Page: general data + financial data.
OData v2 service in SEGW.
Include activation steps in /IWFND/MAINT_SERVICE.`,
    tip: 'Generates CDS view + metadata.xml + annotations.xml + OData service + activation steps.',
  },
  {
    id: '10', icon: '🐛', title: 'SAP - ST22 RUNTIME ANALYSIS',
    desc: 'Analyzes Short Dumps and identifies root cause with technical solution.',
    steps: ['Go to SAP - ST22 RUNTIME ANALYSIS', 'Paste the dump content into the AI REQUIREMENT field', 'Click ANALYZE DUMP'],
    example: `Short Dump: SYSTEM_NO_ROLL
Program: ZFI_AR_AUTO_CLEARING
Include: ZFI_AR_AUTO_CLEARING_F01
Line: 245
Code at time of error:
SELECT * FROM bseg INTO TABLE lt_bseg
  WHERE bukrs = p_bukrs AND belnr IN lr_belnr.
Variables: LT_BSEG: 2,847,392 records`,
    tip: 'Include as much ST22 information as possible: dump type, variables, call stack.',
  },
  {
    id: '11', icon: '✅', title: 'SAP - ABAP UNIT TESTING',
    desc: 'Creates test classes with CL_ABAP_UNIT_ASSERT to validate business logic.',
    steps: ['Go to SAP - ABAP UNIT TESTING', 'Paste the code to test into the AI REQUIREMENT field', 'Click GENERATE UNIT TESTS'],
    example: `Generate unit tests for class lcl_clearing_processor with these methods:
- calculate_score: receives email_amount, bank_amount, email_date, bank_date. Returns score 0-100.
- validate_total: receives header_total and line item table. Returns abap_bool.
- determine_action: receives score. Returns AUTOMATIC/MANUAL/ERROR.
Cover normal cases, boundaries and errors.`,
    tip: 'The engine generates setup/teardown and at least 3 cases per method with assertions.',
  },
  {
    id: '12', icon: '🔐', title: 'SAP - AUTHORIZATION CHECK',
    desc: 'Detects accesses without AUTHORITY-CHECK and adds ABAP security.',
    steps: ['Go to SAP - AUTHORIZATION CHECK', 'Paste the code to audit into the AI REQUIREMENT field', 'Click AUDIT SECURITY'],
    example: `FORM frm_read_invoices.
  SELECT bukrs, belnr, gjahr, dmbtr
    INTO TABLE @gt_bkpf FROM bkpf
    WHERE bukrs = @p_bukrs.
ENDFORM.
FORM frm_update_status.
  UPDATE zpay_email_hdr SET status = @iv_status
    WHERE id_email = @iv_id.
ENDFORM.
FORM frm_delete_record.
  DELETE FROM zmatch_log WHERE id = @iv_id.
ENDFORM.`,
    tip: 'The engine identifies which SAP authorization object corresponds to each operation.',
  },
  {
    id: '13', icon: '📄', title: 'SAP - TECHNICAL BLUEPRINT',
    desc: 'Generates professional technical design documents in Markdown.',
    steps: ['Go to SAP - TECHNICAL BLUEPRINT', 'Describe the program in the AI REQUIREMENT field', 'Click GENERATE BLUEPRINT'],
    example: `Technical document for ZFI_AR_AUTO_CLEARING.
Module: FI-AR / Bank Accounting.
Z Tables: ZPAY_EMAIL_HDR, ZPAY_EMAIL_POS, ZCLIENT_EMAIL, ZBANK_MATCH, ZMATCH_LOG.
Standard tables: BSID, FEBEP, KNA1.
Version: S/4HANA 2021+.
Process: 8 steps with matching and scoring.
Author: [Your name]. Estimate: 40 hours.`,
    tip: 'Generates field tables, relationship matrix, selection screen and validations.',
  },
  {
    id: '14', icon: '🔗', title: 'SAP - API DATA MAPPING',
    desc: 'Converts JSON to ABAP TYPES structures with deserialization.',
    steps: ['Go to SAP - API DATA MAPPING', 'Paste the JSON into the AI REQUIREMENT field', 'Click MAP JSON TO ABAP'],
    example: `{
  "paymentNotification": {
    "id": "550e8400-e29b-41d4-a716",
    "sender": {
      "email": "vendor@company.com",
      "taxId": "30-12345678-9"
    },
    "header": {
      "totalAmount": 15750.50,
      "currency": "USD"
    },
    "status": "PENDING"
  }
}`,
    tip: 'Generates ABAP TYPES, deserialization with /ui2/cl_json and validation against SAP tables.',
  },
  {
    id: '15', icon: '🌐', title: 'SAP - CODE TRANSLATION',
    desc: 'Translates comments and messages from English to SAP technical Spanish.',
    steps: ['Go to SAP - CODE TRANSLATION', 'Paste the English code into the AI REQUIREMENT field', 'Click TRANSLATE CODE'],
    example: `* Select pending payment notices
FORM frm_select_payments.
  * Read header table
  SELECT mandt, id_email, email_from, total
    INTO TABLE @gt_pay_hdr
    FROM zpay_email_hdr
    WHERE status = @gc_status_pending.
  * Validate results
  IF sy-subrc <> 0.
    * No records found
    MESSAGE i001 WITH 'No pending payments'.
  ENDIF.
ENDFORM.`,
    tip: 'Translates ONLY comments and messages — code, variables and syntax are not touched.',
  },
  {
    id: '16', icon: '📋', title: 'SAP - DOCUMENT GENERATION',
    desc: 'Generates step-by-step user manuals in Markdown.',
    steps: ['Go to SAP - DOCUMENT GENERATION', 'Describe the program in the AI REQUIREMENT field', 'Click GENERATE DOCUMENT'],
    example: `User manual for ZFI_AR_AUTO_CLEARING.
The program processes email payment notices and automatically clears invoices.
Steps: run SE38, enter company and dates, activate TEST mode, view result in ALV.
ALV shows: Email, Customer, Score, Status.
Score >= 80: automatic. Score >= 50: manual.
Messages: 'No notices', 'Customer not identified', 'Successful clearing', 'Clearing error'.`,
    tip: 'Generates numbered steps with indicated screenshots, system messages and complete FAQ.',
  },
  {
    id: '17', icon: '🔍', title: 'SAP - SPEC VALIDATOR',
    desc: 'Analyzes functional documents and evaluates completeness against the standard 10-section model.',
    steps: ['Go to SAP - SPEC VALIDATOR', 'Upload the functional document (PDF or Word)', 'Click VALIDATE SPECIFICATION', 'View the report with score and section detail', 'Optionally click GENERATE CORRECTED DOCUMENT'],
    example: `Upload any SAP functional document in PDF or Word format.
The validator evaluates the 10 standard sections:
1. Development Objective
2. Requester Data
3. Functional Scope
4. Business Rules
5. Process Flow
6. Interfaces and Forms
7. Data Model / SAP Tables
8. Reports and Outputs
9. Security and Authorizations
10. Acceptance Criteria`,
    tip: 'Score ranges from 0 to 100%. Documents with score < 50% can be automatically regenerated.',
  },
  {
    id: '18', icon: '🔷', title: 'SAP - RAP ARCHITECT',
    desc: 'Generates the complete S/4HANA RAP stack with 13 objects ready to activate in Eclipse ADT.',
    steps: [
      'Go to SAP - RAP ARCHITECT',
      'Step 1 — Base Entity: enter name, DDIC table, key fields and data fields',
      'Step 2 — CDS Stack: define ZI_, ZR_, ZC_ names and enable Draft for Fiori draft handling',
      'Step 3 — Behavior: select CRUD operations, validations and custom actions',
      'Step 4 — Service: define Service Definition and Binding names, select OData V4',
      'Click GENERATE and explore the 13 objects in the RAP Stack Explorer',
    ],
    example: `Entity: SalesOrder | Table: VBAK
Key fields: Id (abap.uuid), Vbeln (vbeln)
Data fields: Kunnr (kunnr), Erdat (abap.dats), Netwr (abap.dec(15,2)), Waers (waers)
Draft: enabled
Validations: validateNetAmount, validateCustomer
Service: ZSD_SALESORDER → ZSB_SALESORDER_V4 (OData V4)

Generated objects:
- ZSALESORDER + ZSALESORDER_D (active and draft tables)
- ZI_SALESORDER (Interface View)
- ZR_SALESORDER (Root BO View)
- ZC_SALESORDER (Projection View with transactional_ui)
- ZC_SALESORDER.ddlx (Metadata Extension with full @UI)
- ZR_SALESORDER.bdef (managed + strict(2) + with draft)
- ZBP_R_SALESORDER (Behavior Implementation with validations)
- ZC_SALESORDER.bdef (Projection BDEF with use draft)
- ZI_SALESORDER.dcl + ZC_SALESORDER.dcl (Access Control)
- ZSD_SALESORDER (Service Definition)
- ZSB_SALESORDER_V4 (Service Binding OData V4)`,
    tip: 'Activate in Eclipse ADT in order: Tables → ZI_ → ZR_ → ZC_ → Metadata Ext → BDEF → Service. Wrong order causes activation errors.',
  },
  {
    id: '19', icon: '☁️', title: 'SAP - CAP ARCHITECT',
    desc: 'Generates the complete CAP stack for SAP BTP with 13 files ready for cds watch and deploy.',
    steps: [
      'Go to SAP - CAP ARCHITECT',
      'Step 1 — Project: enter name, runtime (Node.js/Java), database (SQLite/HANA) and namespace',
      'Step 2 — Model: define entities with their fields and CDS types',
      'Step 3 — Service: configure service name, OData version and Fiori annotations',
      'Step 4 — Deploy: enable mta.yaml, XSUAA and AppRouter for BTP deployment',
      'Click GENERATE and explore the 13 files in the CAP Stack Explorer',
    ],
    example: `Project: orders-app | Runtime: Node.js | DB: SQLite | Namespace: com.orquest

Entity 1 — Orders:
  ID (UUID, key), customerId (String), orderDate (Date), netAmount (Decimal), status (String)

Entity 2 — OrderItems:
  ID (UUID, key), quantity (Integer), unitPrice (Decimal)

Service: CatalogService | OData V4 | Fiori Annotations: ✅

Generated files:
- db/schema.cds (entities with compositions and enums)
- srv/service.cds (OData V4 service definition)
- srv/annotations.cds (Fiori annotations: HeaderInfo, LineItem, Facets)
- srv/service.js (before/after handlers + validations + actions)
- package.json (correct @sap/cds dependencies)
- mta.yaml (srv, db, approuter modules for BTP)
- xs-security.json (XSUAA roles and scopes)
- .cdsrc.json (SQLite local / HANA production)
- .gitignore + .env (local configuration)
- db/data/Orders.csv + db/data/OrderItems.csv (test data)
- README.md (deploy steps with cf and MTA)

To run locally: npm install → cds watch → http://localhost:4004`,
    tip: 'Use SQLite for local development (no configuration needed) and switch to HANA in production by changing only the .cdsrc.json.',
  },
];

// ─── ToolCard ─────────────────────────────────────────────────────────────────

const ToolCard = ({ tool, lang }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(tool.example);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-white/8 rounded-2xl overflow-hidden transition-all duration-300 bg-white/3 hover:border-white/15">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors text-left"
      >
        <div className="flex items-center gap-4">
          <span className="text-2xl shrink-0">{tool.icon}</span>
          <div>
            <p className="text-[13px] font-black text-white uppercase tracking-wide">{tool.title}</p>
            <p className="text-[11px] text-white/40 mt-0.5 leading-relaxed">{tool.desc}</p>
          </div>
        </div>
        <div className="shrink-0 ml-3 text-white/30">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
          <div>
            <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.15em] mb-3">
              {lang === 'es' ? 'Cómo probarlo' : 'How to test it'}
            </p>
            <ol className="space-y-2">
              {tool.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-[12px] text-white/60">
                  <span className="text-blue-400 font-black shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.15em]">
                {lang === 'es' ? 'Ejemplo para copiar y pegar' : 'Copy & paste example'}
              </p>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 transition-all uppercase tracking-wider"
              >
                {copied ? <Check size={11} /> : <Copy size={11} />}
                {copied ? (lang === 'es' ? 'Copiado' : 'Copied') : (lang === 'es' ? 'Copiar' : 'Copy')}
              </button>
            </div>
            <pre className="text-[12px] text-emerald-300 bg-black/40 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap leading-relaxed font-mono border border-white/5">
              {tool.example}
            </pre>
          </div>

          <div className="flex gap-3 bg-amber-500/8 border border-amber-500/15 rounded-xl p-4">
            <span className="text-base shrink-0">💡</span>
            <p className="text-[12px] text-amber-300/80 leading-relaxed italic">{tool.tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const UserGuideViewer = ({ isOpen, onClose, inline = false }) => {
  const [lang, setLang] = useState('es');
  const [search, setSearch] = useState('');

  const tools = lang === 'es' ? TOOLS_ES : TOOLS_EN;
  const filtered = tools.filter(t =>
    t.title.toLowerCase().includes(search.toLowerCase()) ||
    t.desc.toLowerCase().includes(search.toLowerCase())
  );

  if (!isOpen) return null;

  // ── MODO INLINE — workspace completo ─────────────────────────────────────
  if (inline) {
    return (
      <div className="max-w-5xl mx-auto animate-in fade-in duration-500 pb-20">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-white">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-slate-500 font-black text-[10px] uppercase hover:text-blue-400 transition-all tracking-widest"
          >
            <ArrowLeft size={14} /> Workspace Central
          </button>
          <span className="text-slate-700 text-[11px]">›</span>
          <span className="text-[11px] font-black text-white uppercase tracking-widest">
            {lang === 'es' ? 'Guía de Usuario' : 'User Guide'}
          </span>
        </div>

        {/* Card principal */}
        <div className="bg-white/5 backdrop-blur-3xl rounded-[40px] shadow-2xl border border-white/10 overflow-hidden">

          {/* Header */}
          <div className="p-8 border-b border-white/5 bg-white/5 px-10">
            <div className="flex items-center gap-4">
              <div className="p-5 rounded-3xl bg-blue-600 text-white shadow-xl shadow-blue-500/20">
                <BookOpen size={24} />
              </div>
              <div>
                <h2 className="text-3xl font-black text-white uppercase italic underline decoration-blue-500/30 decoration-4 underline-offset-4">
                  {lang === 'es' ? 'Guía de Usuario' : 'User Guide'}
                </h2>
                <p className="text-[10px] text-blue-400 font-black uppercase tracking-wider mt-1">
                  ORQUEST SAP Architect · 17 {lang === 'es' ? 'herramientas disponibles' : 'tools available'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-10">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              <div className="flex bg-white/5 border border-white/8 rounded-xl overflow-hidden shrink-0">
                {['es', 'en'].map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider transition-all ${lang === l ? 'bg-blue-600 text-white' : 'text-white/30 hover:text-white'
                      }`}
                  >
                    {l === 'es' ? '🇦🇷 Español' : '🇺🇸 English'}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder={lang === 'es' ? 'Buscar herramienta...' : 'Search tool...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 w-full bg-[#0B0F1A] border-2 border-blue-500 
                rounded-[20px] px-6 py-3 text-[13px] text-slate-300 
                placeholder:text-slate-500 outline-none transition-all shadow-lg shadow-blue-500/20"
              />
              <span className="text-[10px] text-white/25 uppercase tracking-widest shrink-0">
                {filtered.length} {lang === 'es' ? 'herramientas' : 'tools'}
              </span>
            </div>

            {/* Grid 2 columnas */}
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-white/20 text-[14px]">
                {lang === 'es' ? 'No se encontraron herramientas' : 'No tools found'}
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {filtered.map(tool => (
                  <ToolCard key={tool.id} tool={tool} lang={lang} />
                ))}
              </div>
            )}

            <p className="text-[9px] text-white/15 text-center uppercase tracking-widest mt-10">
              ORQUEST SAP Architect v5.6.2 · {filtered.length} {lang === 'es' ? 'herramientas disponibles' : 'tools available'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── MODO MODAL — original sin cambios ─────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-1 backdrop-blur-sm bg-black/70">
      <div className="bg-[#0B0F1A] border border-white/8 w-full max-w-4xl rounded-[32px] shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

        <div className="relative p-5 border-b border-white/5 shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/8 to-transparent rounded-t-[32px]" />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <BookOpen size={17} className="text-white" />
              </div>
              <div>
                <h3 className="text-[13px] font-black uppercase tracking-widest text-white leading-none">
                  {lang === 'es' ? 'Guía de Usuario' : 'User Guide'}
                </h3>
                <p className="text-[10px] text-blue-400 font-black uppercase tracking-wider mt-0.5">
                  ORQUEST SAP Architect · 17 {lang === 'es' ? 'herramientas' : 'tools'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-white/5 border border-white/8 rounded-xl overflow-hidden">
                {['es', 'en'].map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all ${lang === l ? 'bg-blue-600 text-white' : 'text-white/30 hover:text-white'
                      }`}
                  >
                    {l === 'es' ? '🇦🇷 ES' : '🇺🇸 EN'}
                  </button>
                ))}
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all"
              >
                <X size={15} />
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder={lang === 'es' ? 'Buscar herramienta...' : 'Search tool...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#0B0F1A] border-2 border-blue-500 
            rounded-[20px] px-5 py-3 text-[12px] text-slate-300 
            placeholder:text-slate-500 outline-none transition-all shadow-lg shadow-blue-500/20"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-hide">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-white/20 text-[12px]">
              {lang === 'es' ? 'No se encontraron herramientas' : 'No tools found'}
            </div>
          ) : (
            filtered.map(tool => (
              <ToolCard key={tool.id} tool={tool} lang={lang} />
            ))
          )}
        </div>

        <div className="p-4 border-t border-white/5 shrink-0">
          <p className="text-[9px] text-white/15 text-center uppercase tracking-widest">
            ORQUEST SAP Architect v5.6.2 · {filtered.length} {lang === 'es' ? 'herramientas disponibles' : 'tools available'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserGuideViewer;